/**
 * CaseTrackUS - Terms of Service Page
 * Updated: May 26, 2026
 * Author: Irina Panferova | Panferov LLC
 * Copyright: (c) 2026 Panferov LLC. All rights reserved.
 */

import React from 'react';
import { Container, Box, Typography, Button, Divider } from '@mui/material';

const P = ({ children, sx = {} }) => (
  <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8, color: '#2C2C2C', fontSize: '0.95rem', ...sx }}>
    {children}
  </Typography>
);

const Li = ({ children }) => (
  <Typography component="li" variant="body1" sx={{ mb: 0.75, lineHeight: 1.8, color: '#2C2C2C', fontSize: '0.95rem', ml: 2 }}>
    {children}
  </Typography>
);

const Section = ({ number, title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 2, pb: 1, borderBottom: '2px solid #1B5E20' }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1B5E20', letterSpacing: 3, textTransform: 'uppercase', minWidth: 24 }}>
        {number}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A1A1A', letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.9rem' }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

const Sub = ({ title, children }) => (
  <Box sx={{ mb: 2.5, pl: 2, borderLeft: '3px solid #E8F5E9' }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2E7D32', mb: 1, fontSize: '0.9rem' }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const Alert = ({ children }) => (
  <Box sx={{ background: '#FFF8E1', border: '1px solid #F9A825', borderRadius: 1, p: 2.5, mb: 3 }}>
    <Typography variant="body2" sx={{ color: '#5D4037', lineHeight: 1.8, fontSize: '0.9rem' }}>
      {children}
    </Typography>
  </Box>
);

const DefItem = ({ term, children }) => (
  <Box sx={{ mb: 2, display: 'grid', gridTemplateColumns: '200px 1fr', gap: 2 }}>
    <Typography sx={{ fontWeight: 700, color: '#1B5E20', fontSize: '0.9rem', pt: 0.2 }}>{term}</Typography>
    <Typography sx={{ color: '#2C2C2C', lineHeight: 1.8, fontSize: '0.9rem' }}>{children}</Typography>
  </Box>
);

const PriceRow = ({ plan, items, price, note }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr 130px', gap: 0, borderBottom: '1px solid #E8F5E9' }}>
    <Box sx={{ p: 1.5, borderRight: '1px solid #E8F5E9' }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1B5E20' }}>{plan}</Typography>
      {note && <Typography sx={{ fontSize: '0.75rem', color: '#666', mt: 0.3 }}>{note}</Typography>}
    </Box>
    <Box sx={{ p: 1.5, borderRight: '1px solid #E8F5E9' }}>
      <Typography sx={{ fontSize: '0.85rem', color: '#2C2C2C' }}>{items}</Typography>
    </Box>
    <Box sx={{ p: 1.5 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1A1A1A' }}>{price}</Typography>
    </Box>
  </Box>
);

export default function Terms() {
  return (
    <Box sx={{ minHeight: '100vh', background: '#FAFAFA' }}>

      {/* Header */}
      <Box sx={{ background: '#1A1A1A', color: 'white', py: 2.5 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', letterSpacing: 1 }}>🗽 CASETRACKUS</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#aaa', letterSpacing: 2, textTransform: 'uppercase' }}>by Panferov LLC</Typography>
            </Box>
            <Button href="/" variant="outlined" size="small"
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', letterSpacing: 1 }}>
              ← Back to App
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Title block */}
      <Box sx={{ background: '#1B5E20', color: 'white', py: 5 }}>
        <Container maxWidth="md">
          <Typography sx={{ fontSize: '0.7rem', letterSpacing: 4, textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', mb: 1 }}>
            Legal Document
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 1.5, letterSpacing: -0.5 }}>
            Terms of Service
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>
              Effective: March 17, 2026
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>
              Last Updated: May 26, 2026
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>
              Panferov LLC · Illinois, USA
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 6 }}>

        {/* Important Notice */}
        <Box sx={{ background: '#FFF3E0', border: '2px solid #E65100', borderRadius: 1, p: 3, mb: 5 }}>
          <Typography sx={{ fontWeight: 800, color: '#E65100', fontSize: '0.75rem', letterSpacing: 3, textTransform: 'uppercase', mb: 1.5 }}>
            ⚠ Important Legal Notice
          </Typography>
          <P>CaseTrackUS is NOT a law firm and does NOT provide legal services. We are an independent technology monitoring service. Nothing in these Terms or on our platform constitutes legal advice, legal representation, or a guarantee of any immigration outcome.</P>
          <P>CaseTrackUS retrieves information from official government websites (USCIS, EOIR, DOL). Government systems may not reflect the actual state of affairs — including due to technical failures, human error, administrative delays, or discrepancies between physical documents and online systems.</P>
          <P sx={{ mb: 0 }}>The absence of information about a case, hearing, or procedural event in a government online system does not mean that such event does not exist. Procedural deadlines — including appeal filing deadlines — are calculated from the moment of the legally significant event, not from when the status updates online.</P>
        </Box>

        {/* Section 1 */}
        <Section number="01" title="Introduction and Acceptance of Terms">
          <P>Welcome to CaseTrackUS — an automated immigration case status monitoring platform operated by Panferov LLC ("Company", "we", "us", "our"), a limited liability company organized under the laws of the State of Illinois, USA.</P>
          <P>These Terms of Service ("Terms") constitute a legally binding agreement between you ("User", "you", "your") and Panferov LLC governing your access to the CaseTrackUS website at https://casetrackus.com, all associated mobile applications, browser extensions, APIs, and related services (collectively, the "Service").</P>
          <P>The Service is intended for two categories of users: <strong>Individual users</strong> — immigrants and their family members independently tracking their own immigration cases; and <strong>Professional users</strong> — licensed immigration attorneys, accredited representatives, and law firms.</P>
          <Box sx={{ background: '#F3F4F6', border: '1px solid #D1D5DB', borderRadius: 1, p: 2.5, my: 2 }}>
            <P sx={{ fontWeight: 700, mb: 1 }}>BY ACCESSING OR USING THE SERVICE, YOU AGREE TO BE BOUND BY THESE TERMS AND OUR PRIVACY POLICY. IF YOU DO NOT AGREE, YOU MUST NOT USE THE SERVICE.</P>
          </Box>
          <P>We reserve the right to modify these Terms at any time. We will notify you of material changes via email at least 14 days before changes take effect.</P>
        </Section>

        {/* Section 2 */}
        <Section number="02" title="Definitions">
          <DefItem term='"Service"'>The CaseTrackUS website, applications, browser extensions, APIs, and all related features operated by Panferov LLC.</DefItem>
          <DefItem term='"Tracked Item"'>A single unique immigration case identifier added to the Service for monitoring. Each USCIS receipt number counts as 1 Tracked Item. Each EOIR A-Number counts as 1 Tracked Item. One individual may have multiple Tracked Items simultaneously. Example: a family of 4 each with an EOIR court case and a work permit = 8 Tracked Items total.</DefItem>
          <DefItem term='"Browser Extension"'>The CaseTrackUS EOIR Checker Chrome extension that automates retrieval of EOIR ACIS case information on behalf of the Account holder, operating on devices under the Account holder's direct control.</DefItem>
          <DefItem term='"Individual Account"'>An account of an individual immigrant tracking their own cases and family members' cases. The account owner bears sole responsibility for accuracy of added data.</DefItem>
          <DefItem term='"Professional Account"'>An account of a licensed immigration attorney, accredited representative, or law firm. The professional user bears sole responsibility for client agreements and professional obligations.</DefItem>
          <DefItem term='"Family Member"'>An individual whose profile is created within an Individual Account by the account owner.</DefItem>
          <DefItem term='"Client"'>An individual whose cases are tracked through a Professional Account.</DefItem>
          <DefItem term='"Case Data"'>Identification, procedural, and status information retrieved from Government Sources plus data entered by the user. The user bears full responsibility for accuracy of entered data — including wrong receipt numbers, incorrect A-Numbers, or incorrect nationality. For EOIR cases, data must match exactly what is recorded in the court system.</DefItem>
          <DefItem term='"Government Sources"'>Official US government databases including USCIS Torch API, EOIR ACIS, DOL FLAG system, and USCIS FOIA API. CaseTrackUS is not responsible for accuracy or availability of Government Source data.</DefItem>
          <DefItem term='"Beta Period"'>The period of full free access to all Service features. Duration announced at least 30 days before its end.</DefItem>
          <DefItem term='"Advertising"'>Third-party advertisements displayed within the Service to support free access.</DefItem>
          <DefItem term='"Content"'>All text, data, information, graphics, and other materials available through the Service.</DefItem>
        </Section>

        {/* Section 3 */}
        <Section number="03" title="Service Description and Government Data Sources">
          <Sub title="3.1 What CaseTrackUS Does">
            <P>CaseTrackUS is an automated monitoring platform that:</P>
            <ul>
              <Li><strong>USCIS Case Tracking:</strong> retrieves official status of USCIS applications including form type, current status, and processing history</Li>
              <Li><strong>EOIR Court Case Tracking:</strong> retrieves publicly available information about immigration court cases including hearing dates, judge decisions, BIA appeal statuses via EOIR ACIS</Li>
              <Li><strong>DOL Labor Case Tracking:</strong> retrieves publicly available labor condition application information</Li>
              <Li><strong>FOIA Requests:</strong> submits FOIA/PA requests and tracks their status</Li>
              <Li>Sends email notifications when case status changes are detected</Li>
              <Li>Performs scheduled automatic case status checks</Li>
            </ul>
          </Sub>
          <Sub title="3.2 Chrome Extension — EOIR Case Monitoring">
            <P>The CaseTrackUS EOIR Checker browser extension automates retrieval of publicly available EOIR ACIS information. The extension:</P>
            <ul>
              <Li>Operates only on authorized devices under the direct control of the Account holder</Li>
              <Li>Accesses only publicly available information using case identifiers you provided</Li>
              <Li>Transmits retrieved data exclusively to CaseTrackUS servers</Li>
              <Li>Does not access non-public, restricted, or confidential government information</Li>
              <Li>Does not access or collect data from any website other than acis.eoir.justice.gov</Li>
            </ul>
          </Sub>
          <Sub title="3.3 Government Data Sources">
            <ul>
              <Li><strong>USCIS Case Status API</strong> (developer.uscis.gov) — official USCIS case status</Li>
              <Li><strong>USCIS FOIA API</strong> (developer.uscis.gov) — FOIA/PA request status; documents delivered to your USCIS account, not stored by CaseTrackUS</Li>
              <Li><strong>EOIR ACIS</strong> (acis.eoir.justice.gov) — court case information. Note: EOIR ACIS may not reflect reality due to NTA registration delays or technical failures</Li>
              <Li><strong>DOL FLAG</strong> (flag.dol.gov) — labor condition application information</Li>
            </ul>
          </Sub>
          <Sub title="3.4 What CaseTrackUS Does NOT Do">
            <ul>
              <Li>Provide legal advice, representation, or assistance of any kind</Li>
              <Li>Guarantee any immigration outcome or processing timeline</Li>
              <Li>Access non-public or confidential government information</Li>
              <Li>Submit immigration documents on your behalf (except FOIA requests)</Li>
              <Li>Represent your interests before any government agency</Li>
              <Li>Have official affiliation with USCIS, EOIR, DOL, DHS, DOJ, BIA, or any US government agency</Li>
              <Li>Bear responsibility for missed procedural deadlines, hearings, or legally significant events</Li>
            </ul>
          </Sub>
        </Section>

        {/* Section 4 */}
        <Section number="04" title="Advertising">
          <Sub title="4.1 Display of Advertisements">
            <P>The Service may display third-party advertisements, including advertisements from immigration attorneys and law firms, to support provision of free or reduced-cost access. Advertisements are served by third parties governed by their own privacy policies. We do not allow advertisers to target advertisements based on your immigration case data, A-Numbers, receipt numbers, or immigration status.</P>
          </Sub>
          <Sub title="4.2 Attorney and Law Firm Advertising">
            <P>CaseTrackUS may display advertisements from licensed immigration attorneys and law firms. By displaying such advertisements, CaseTrackUS does NOT endorse any advertised attorney, does NOT establish any attorney-client relationship, and is NOT responsible for the quality of advertised legal services. Nothing in an advertisement constitutes legal advice. Users are solely responsible for independently verifying attorney credentials.</P>
          </Sub>
          <Sub title="4.3 Advertising Standards">
            <ul>
              <Li>No advertisements that could be mistaken for official government communications</Li>
              <Li>No deceptive or predatory advertising targeting vulnerable immigrant populations</Li>
              <Li>No advertising targeted based on your specific immigration case data</Li>
              <Li>All advertisements clearly labeled as "Advertisement" or "Sponsored"</Li>
              <Li>Attorney advertisements must comply with applicable state bar advertising rules</Li>
            </ul>
          </Sub>
          <Sub title="4.4 Ad-Free Options">
            <P>All paid plans (Solo Attorney and above) are ad-free. We may offer additional ad-free options in the future at casetrackus.com/pricing.</P>
          </Sub>
          <Sub title="4.5 Advertising Opt-Out">
            <P>You may limit interest-based advertising through your browser's cookie settings or industry opt-out tools. Opting out means ads will not be personalized, but you may still see non-targeted advertisements on the free plan. We never use your immigration case data for advertising targeting regardless of your opt-out status.</P>
          </Sub>
        </Section>

        {/* Section 5 */}
        <Section number="05" title="Registration and Account Security">
          <Sub title="5.1 Age Requirement">
            <P>You must be at least 18 years old to create an account. Immigration cases of minors may be tracked through the account of their parent, legal guardian, or professional user.</P>
          </Sub>
          <Sub title="5.2 Account Registration">
            <P>You must provide accurate, current, and complete information. For Professional Accounts, you must provide accurate information about your professional status. Providing false information about professional status is grounds for immediate termination.</P>
          </Sub>
          <Sub title="5.3 Account Security">
            <P>You bear sole responsibility for maintaining the confidentiality of your credentials. Notify us immediately at support@casetrackus.com if you suspect unauthorized access. Professional users bear special responsibility for account security given that it contains client case data.</P>
          </Sub>
          <Sub title="5.4 Account Deletion">
            <P>You may delete your account at any time through Account Settings. Upon deletion: your personal data, cases, family member profiles, and case history are permanently and irreversibly deleted immediately. We retain only payment records as required by tax law (up to 7 years) and anonymized statistical data that cannot be linked to you.</P>
          </Sub>
          <Sub title="5.5 Number of Accounts">
            <P>Each individual may only have one Account. For law firms, one Professional Account may be used by multiple employees within one organization.</P>
          </Sub>
        </Section>

        {/* Section 6 */}
        <Section number="06" title="Account Sharing and Transfer">
          <Sub title="6.1 Individual Accounts">
            <P>Individual Accounts are personal and non-transferable. You may not share your login credentials with any other person.</P>
          </Sub>
          <Sub title="6.2 Professional Accounts">
            <P>Professional Accounts may be used by multiple authorized employees within the same law firm or organization, provided the account owner remains responsible for all use and credentials are not shared outside the organization.</P>
          </Sub>
          <Sub title="6.3 Account Transfer">
            <P>Accounts may not be sold, transferred, or assigned to another individual or entity without our prior written consent.</P>
          </Sub>
        </Section>

        {/* Section 7 */}
        <Section number="07" title="Beta Period Disclaimer">
          <P>The Service is currently in beta testing. During the Beta Period:</P>
          <ul>
            <Li>Features may be incomplete, contain bugs, or change without notice</Li>
            <Li>The Service may experience downtime, data loss, or unexpected behavior</Li>
            <Li>We make no guarantees about uptime, performance, or feature availability</Li>
            <Li>We may add, modify, or remove features at any time without notice</Li>
          </ul>
          <Alert>By using the Service during the Beta Period, you acknowledge these limitations and agree that CaseTrackUS shall not be liable for any issues arising from beta software, including data loss, service interruptions, or changes to functionality.</Alert>
        </Section>

        {/* Section 8 */}
        <Section number="08" title="Pricing and Payment">
          <Sub title="8.1 Beta Period">
            <P>During the Beta Period, all Service features are available free of charge. We will notify all registered users at least 30 days before the Beta Period ends and paid plans are introduced.</P>
          </Sub>
          <Sub title="8.2 Pricing Plans">
            <P>After the Beta Period, the following monthly subscription plans will be available. All plans include USCIS and EOIR case tracking, email notifications, and status history.</P>

            {/* Price table */}
            <Box sx={{ border: '1px solid #C8E6C9', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr 130px', background: '#1B5E20', color: 'white' }}>
                <Box sx={{ p: 1.5 }}><Typography sx={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: 1 }}>PLAN</Typography></Box>
                <Box sx={{ p: 1.5, borderLeft: '1px solid rgba(255,255,255,0.2)' }}><Typography sx={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: 1 }}>TRACKED ITEMS</Typography></Box>
                <Box sx={{ p: 1.5, borderLeft: '1px solid rgba(255,255,255,0.2)' }}><Typography sx={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: 1 }}>PRICE/MO</Typography></Box>
              </Box>
              <PriceRow plan="Individual" note="Ad-supported" items="Up to 5 Tracked Items" price="Free" />
              <PriceRow plan="Solo Attorney" items="Up to 30 Tracked Items" price="$29/month" />
              <PriceRow plan="Small Firm" items="Up to 100 Tracked Items" price="$69/month" />
              <PriceRow plan="Law Firm" items="Up to 300 Tracked Items" price="$149/month" />
              <PriceRow plan="Large Firm" items="Up to 1,000 Tracked Items" price="$299/month" />
              <PriceRow plan="Enterprise" items="1,000+ Tracked Items" price="Contact us" />
            </Box>

            <P>All prices in USD, excluding applicable taxes. No annual commitment required. Enterprise pricing available at support@casetrackus.com.</P>
            <P><strong>Regarding EOIR Tracked Items:</strong> EOIR case status is retrieved via the CaseTrackUS browser extension. EOIR checks are performed weekly. In the event of technical issues with the browser extension, EOIR checks may be temporarily delayed; users will be notified of any such delays. EOIR monitoring interruptions do not constitute grounds for refunds unless interruption exceeds 30 consecutive days.</P>
          </Sub>
          <Sub title="8.3 How Tracked Items Are Counted">
            <P>The number of Tracked Items is the total number of active unique identifiers across all family members or clients in your account:</P>
            <ul>
              <Li>Each USCIS receipt number = 1 Tracked Item</Li>
              <Li>Each EOIR A-Number = 1 Tracked Item</Li>
              <Li>Inactive or deleted identifiers are not counted</Li>
            </ul>
            <P><strong>Example:</strong> An attorney managing a family of 3, each with an EOIR court case (A-Number) and a pending work permit (receipt number), would have 6 Tracked Items total (3 A-Numbers + 3 receipt numbers).</P>
          </Sub>
          <Sub title="8.4 Plan Changes">
            <P>You may upgrade or downgrade your plan at any time through Account Settings. Upgrades take effect immediately. Downgrades take effect at the start of the next billing period. If your Tracked Item count exceeds your plan limit, you will be notified and required to upgrade or remove items within 7 days.</P>
          </Sub>
          <Sub title="8.5 Cancellation and Refunds">
            <P>You may cancel at any time through Account Settings. Cancellation takes effect at the end of the current billing period. No refunds for unused portions except as required by applicable law. Upon cancellation, your account reverts to the Individual plan if within the free tier limit, or items will be deactivated if count exceeds the limit.</P>
          </Sub>
        </Section>

        {/* Section 9 */}
        <Section number="09" title="FOIA Feature — Required Disclosures">
          <P>When using the FOIA feature, you are submitting a request under the Freedom of Information Act (FOIA) or Privacy Act (PA) directly to USCIS. CaseTrackUS provides technical submission on your behalf but is not a government agency. Your request is governed by 5 U.S.C. § 552 (FOIA) and 5 U.S.C. § 552a (Privacy Act). Documents are delivered to your USCIS account and are not stored by CaseTrackUS.</P>
          <P><strong>Status descriptions:</strong> Submitted (received, awaiting assignment) · Processing (being reviewed) · On Hold (additional information needed) · Closed/Complete (processing finished, check your USCIS account)</P>
        </Section>

        {/* Section 10 */}
        <Section number="10" title="Intellectual Property">
          <Sub title="10.1 Our Intellectual Property">
            <P>The Service, including its design, code, features, text, graphics, logos, and browser extension, is owned by Panferov LLC and protected by US and international copyright, trademark, and other intellectual property laws. The name CaseTrackUS and logo are trade names of Panferov LLC. Reproduction, modification, distribution, reverse engineering, or creation of derivative works without prior written consent is prohibited. IP infringement claims: legal@casetrackus.com.</P>
          </Sub>
          <Sub title="10.2 Your Data">
            <P>You retain full ownership of Case Data you provide. You grant us a limited, non-exclusive, royalty-free, revocable license to process such data solely to provide the Service. We do not sell or commercialize your personal data or case data. This license terminates upon Account deletion.</P>
          </Sub>
          <Sub title="10.3 Originality of Service">
            <P>CaseTrackUS is an original product developed by Panferov LLC based on the founder's personal experience as an immigrant. The Service uses only official public APIs and publicly available government data.</P>
          </Sub>
          <Sub title="10.4 Independence of Service">
            <P>CaseTrackUS is an independent technology service. The fact that a user receives status notifications through the Service does not create any liability of CaseTrackUS to attorneys, representatives, or clients. Attorneys using Professional Accounts independently bear all professional and ethical obligations to their clients.</P>
          </Sub>
          <Sub title="10.5 Feedback and Suggestions">
            <P>If you provide Feedback about the Service, you grant Panferov LLC a non-exclusive, worldwide, royalty-free, irrevocable, perpetual license to use such Feedback without compensation. You represent that your Feedback does not infringe third-party rights.</P>
          </Sub>
        </Section>

        {/* Section 11 */}
        <Section number="11" title="Professional Users — Additional Terms">
          <Sub title="11.1 Professional Responsibility">
            <P>Professional users acknowledge that CaseTrackUS is a supplementary technology tool only. Professional users bear full and exclusive responsibility for: timely tracking all client case statuses through official channels; meeting all procedural deadlines; notifying clients of all legally significant events; maintaining all professional and ethical obligations under applicable bar rules. CaseTrackUS is NOT liable for any missed deadlines, hearings, or procedural events.</P>
          </Sub>
          <Sub title="11.2 No Liability for Client Claims Against Professional Users">
            <P>CaseTrackUS shall not be liable for any claims by clients against professional users arising from the professional user's use of or reliance on the Service, including missed deadlines, missed hearings, or adverse immigration outcomes.</P>
          </Sub>
          <Sub title="11.3 Professional User Indemnification">
            <P>Professional users agree to indemnify and hold harmless Panferov LLC from any claims brought by their clients arising from the professional user's use of the Service.</P>
          </Sub>
          <Sub title="11.4 No Attorney-Client Relationship with CaseTrackUS">
            <P>Use of the Service does not create any attorney-client relationship between CaseTrackUS and any user or third party.</P>
          </Sub>
        </Section>

        {/* Section 12 */}
        <Section number="12" title="Acceptable Use Policy">
          <P>You may use the Service only for lawful purposes. You agree NOT to:</P>
          <ul>
            <Li>Use the Service for any unlawful purpose or in violation of any federal, state, or local law</Li>
            <Li>Attempt unauthorized access to any part of the Service or other user accounts</Li>
            <Li>Use automated tools, bots, or scrapers beyond normal use</Li>
            <Li>Reverse engineer, decompile, or attempt to derive source code from the Service</Li>
            <Li>Reproduce, sell, or resell any part of the Service without express written permission</Li>
            <Li>Transmit malware, viruses, or other harmful code</Li>
            <Li>Impersonate any person or misrepresent your affiliation</Li>
            <Li>Use the Service to track individuals without their knowledge or consent, or for surveillance or harassment</Li>
            <Li>Share, sell, or transfer Account credentials to unauthorized third parties</Li>
            <Li>Attempt to circumvent security measures or rate limits</Li>
            <Li>Submit false, misleading, or fraudulent information</Li>
            <Li>Violate the terms of service of USCIS, EOIR, DOL, or any government source we access on your behalf</Li>
          </ul>
          <P>Violations may result in immediate Account suspension or termination without refund, and may subject you to civil and criminal liability.</P>
        </Section>

        {/* Section 13 */}
        <Section number="13" title="Export Controls and Sanctions Compliance">
          <P>You represent and warrant that: (a) you are not located in a country subject to US government embargo; (b) you are not listed on any US government restricted parties list, including the OFAC Specially Designated Nationals list; (c) you will not use the Service in violation of US export control laws or economic sanctions regulations. We reserve the right to terminate access for any violation of this section.</P>
        </Section>

        {/* Section 14 */}
        <Section number="14" title="Disclaimer of Warranties">
          <Box sx={{ background: '#F3F4F6', border: '1px solid #D1D5DB', borderRadius: 1, p: 2.5 }}>
            <P sx={{ fontWeight: 600 }}>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. CaseTrackUS DOES NOT WARRANT THAT: THE SERVICE WILL OPERATE WITHOUT INTERRUPTION OR ERROR; INFORMATION FROM GOVERNMENT SOURCES WILL BE ACCURATE OR COMPLETE; NOTIFICATIONS WILL BE DELIVERED TIMELY; OR USE OF THE SERVICE WILL PREVENT MISSED PROCEDURAL DEADLINES, HEARINGS, OR OTHER LEGALLY SIGNIFICANT EVENTS.</P>
          </Box>
        </Section>

        {/* Section 15 */}
        <Section number="15" title="Limitation of Liability">
          <Box sx={{ background: '#F3F4F6', border: '1px solid #D1D5DB', borderRadius: 1, p: 2.5, mb: 2 }}>
            <P sx={{ fontWeight: 600 }}>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, PANFEROV LLC SHALL NOT BE LIABLE FOR: LOSS OF DATA, PROFITS, OR BUSINESS OPPORTUNITIES; ANY IMMIGRATION DECISION, DENIAL, DELAY, OR ADVERSE OUTCOME; MISSED PROCEDURAL DEADLINES OR HEARINGS; UNAUTHORIZED ACCESS DUE TO USER FAILURE TO MEET SECURITY REQUIREMENTS; ANY LOSSES FROM INACCURATE DATA ENTERED BY THE USER; OR FOR PROFESSIONAL USERS — ANY LOSSES CAUSED TO CLIENTS DUE TO USE OF THE SERVICE.</P>
            <P sx={{ fontWeight: 600, mb: 0 }}>OUR TOTAL LIABILITY SHALL NOT EXCEED THE GREATER OF: (A) AMOUNTS PAID BY YOU IN THE THREE MONTHS PRECEDING THE EVENT; OR (B) FIFTY DOLLARS USD ($50.00). DURING THE BETA PERIOD, LIABILITY SHALL NOT EXCEED FIFTY DOLLARS USD ($50.00).</P>
          </Box>
          <P>The limitation of liability does not apply to damages caused by willful misconduct or fraud by Panferov LLC.</P>
        </Section>

        {/* Section 16 */}
        <Section number="16" title="Indemnification and Willful Misconduct">
          <P>You agree to indemnify, defend, and hold harmless Panferov LLC, its officers, directors, employees, agents, and affiliates from any claims, losses, liabilities, costs, and expenses (including reasonable attorney fees) arising from: your use of the Service; your violation of these Terms; your violation of applicable law; and for professional users — any client claims arising from use of the Service.</P>
          <P><strong>Willful Misconduct:</strong> If a user intentionally causes harm through deliberate misuse of the Service — including unauthorized access attempts, scraping, reverse engineering, submitting false information, or circumventing security — such user shall be liable for all actual damages, consequential damages, and costs incurred by Panferov LLC, including attorney fees. The limitation of liability in Section 15 does not apply to such conduct.</P>
        </Section>

        {/* Section 17 */}
        <Section number="17" title="Dispute Resolution and Arbitration">
          <Sub title="17.1 Informal Resolution">
            <P>Before any formal proceeding, contact us at legal@casetrackus.com with a written description of the dispute. We will make good faith efforts to resolve within 30 days.</P>
          </Sub>
          <Sub title="17.2 Binding Arbitration">
            <P>If informal resolution fails, disputes will be resolved through binding individual arbitration under AAA Consumer Arbitration Rules, not in court.</P>
          </Sub>
          <Sub title="17.3 Class Action Waiver">
            <Box sx={{ background: '#F3F4F6', border: '1px solid #D1D5DB', borderRadius: 1, p: 2 }}>
              <P sx={{ fontWeight: 600, mb: 0 }}>YOU AND WE EACH EXPRESSLY WAIVE THE RIGHT TO PARTICIPATE IN ANY CLASS ACTION LAWSUIT, CLASS ARBITRATION, OR OTHER REPRESENTATIVE PROCEEDING. ALL DISPUTES MUST BE RESOLVED ON AN INDIVIDUAL BASIS ONLY. THIS WAIVER IS A MATERIAL TERM OF THESE TERMS AND PANFEROV LLC WOULD NOT PROVIDE THE SERVICE WITHOUT IT.</P>
            </Box>
          </Sub>
          <Sub title="17.4 Governing Law">
            <P>These Terms are governed by the laws of the State of Illinois. Court proceedings shall be in Cook County, Illinois. All dispute communications must be in written English.</P>
          </Sub>
          <Sub title="17.5 Statute of Limitations">
            <P>Any claim must be brought within one year of when the claim arose, after which it is permanently barred, except where applicable law requires a longer period.</P>
          </Sub>
        </Section>

        {/* Section 18 */}
        <Section number="18" title="Termination">
          <P>You may close your Account at any time through Account Settings. We may suspend or terminate your Account for violation of these Terms, providing false information, or illegal use. Upon termination, data deletion proceeds as described in Section 5.4.</P>
        </Section>

        {/* Section 19 */}
        <Section number="19" title="Privacy and Data Protection">
          <P>Your use of the Service is governed by our Privacy Policy at https://casetrackus.com/privacy, incorporated into these Terms by reference.</P>
        </Section>

        {/* Section 20 */}
        <Section number="20" title="Third-Party Services">
          <P>The Service integrates with: Resend (email delivery), Google (authentication), Hetzner (cloud infrastructure, Germany), Stripe (payment processing), third-party advertising networks, and official government websites. These services are governed by their own terms and privacy policies. We are not responsible for their practices or failures.</P>
        </Section>

        {/* Section 21 */}
        <Section number="21" title="Force Majeure">
          <P>We are not liable for failure or delay caused by circumstances beyond our reasonable control, including natural disasters, government actions, internet outages, unavailability of government systems (including USCIS, EOIR, and DOL API outages), cyberattacks, or other force majeure events.</P>
        </Section>

        {/* Section 22 */}
        <Section number="22" title="Changes to Service">
          <P>We reserve the right to modify, suspend, or discontinue any aspect of the Service. For material changes to core functionality, we will provide at least 14 days advance notice by email.</P>
        </Section>

        {/* Section 23 */}
        <Section number="23" title="General Provisions">
          <P><strong>Entire Agreement:</strong> These Terms together with the Privacy Policy constitute the complete agreement and supersede all prior agreements.</P>
          <P><strong>Severability:</strong> If any provision is found invalid, remaining provisions continue in full force.</P>
          <P><strong>Waiver:</strong> Failure to enforce any right does not constitute a waiver unless confirmed in writing.</P>
          <P><strong>Assignment:</strong> You may not assign these Terms without our prior written consent. We may assign in connection with a merger, acquisition, or sale of assets.</P>
        </Section>

        {/* Section 24 */}
        <Section number="24" title="Contact Information">
          <P>All communications with CaseTrackUS are conducted exclusively in written form in English. We do not provide phone or live support.</P>
          <Box sx={{ background: '#F9FAF9', border: '1px solid #C8E6C9', borderRadius: 1, p: 3 }}>
            <P sx={{ fontWeight: 700, mb: 0.5 }}>CaseTrackUS, operated by Panferov LLC</P>
            <P sx={{ mb: 0.5 }}>General support: <strong>support@casetrackus.com</strong></P>
            <P sx={{ mb: 0.5 }}>Legal matters: <strong>legal@casetrackus.com</strong></P>
            <P sx={{ mb: 0.5 }}>Privacy questions: <strong>privacy@casetrackus.com</strong></P>
            <P sx={{ mb: 0.5 }}>Website: <strong>https://casetrackus.com</strong></P>
            <P sx={{ mb: 0 }}>Address: Illinois, USA</P>
          </Box>
          <P sx={{ mt: 2, color: '#666', fontSize: '0.85rem' }}>We aim to respond within 5 business days. Urgent security matters: 72 hours.</P>
        </Section>

        <Divider sx={{ my: 4 }} />
        <Box sx={{ textAlign: 'center', pb: 4, display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
          <Button href="/" variant="contained" sx={{ background: '#1B5E20', px: 3 }}>← Back to CaseTrackUS</Button>
          <Button href="/privacy" variant="outlined" sx={{ color: '#1B5E20', borderColor: '#1B5E20', px: 3 }}>Privacy Policy →</Button>
        </Box>
        <Typography sx={{ textAlign: 'center', color: '#999', fontSize: '0.8rem', letterSpacing: 1 }}>
          © 2026 Panferov LLC. All rights reserved. CaseTrackUS is a trade name of Panferov LLC.
        </Typography>

      </Container>
    </Box>
  );
}
